import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {css} from "@panda/css";

import {Text} from "@pg-design/text-module";

import {IOSMPoisRoutesGrouped} from "../../../maps/actions/fetch_osm_pois_routes_grouped";
import {useGetOfferDetailPoiStatsQuery} from "../../../maps/api/get_offer_detail_poi";
import {getRoutes} from "../../../maps/api/get_routes";
import {LocationPoisSummary} from "../../../maps/components/LocationPoisSummary";
import {OpenStreetMapsWithPoi} from "../../../maps/components/map_with_poi/OpenStreetMapsWithPoi";
import {IPublicTransportRoute} from "../../../maps/types/IPublicTransportRoute";
import {IPublicTransportType} from "../../../maps/types/IPublicTransportType";
import {PoiType} from "../../../maps/utils/PoiType";
import {IPropertyDetail} from "../../../property/types/IPropertyDetail";
import {ViewType} from "../../../view_type/ViewType";
import {trackTinderMultiLeadMapInteraction} from "./tracking/track_tinder_multi_lead_events";

const ROUTES_RADIUS = 3000;

interface IProps {
    property: IPropertyDetail;
}

export function TinderMultiLeadLocationSection({property}: IProps) {
    const offerId = property.offer.id;
    const longitude = property.offer.geo_point.coordinates[0];
    const latitude = property.offer.geo_point.coordinates[1];
    const {data: statsData} = useGetOfferDetailPoiStatsQuery(offerId);
    const [activePoiRoute, setActivePoiRoute] = useState<IPublicTransportRoute | null>(null);
    const [routesGrouped, setRoutesGrouped] = useState<IOSMPoisRoutesGrouped | null>(null);
    const mapInteractionTrackedRef = useRef(false);
    const mapWrapperRef = useRef<HTMLDivElement>(null);

    const trackMapInteractionOnce = useCallback(() => {
        if (mapInteractionTrackedRef.current) {
            return;
        }

        mapInteractionTrackedRef.current = true;
        trackTinderMultiLeadMapInteraction({property});
    }, [property]);

    useEffect(() => {
        const node = mapWrapperRef.current;

        if (!node) {
            return;
        }

        const handleInteraction = () => trackMapInteractionOnce();

        node.addEventListener("pointerdown", handleInteraction, {capture: true});
        node.addEventListener("wheel", handleInteraction, {capture: true, passive: true});
        node.addEventListener("touchstart", handleInteraction, {capture: true, passive: true});

        return () => {
            node.removeEventListener("pointerdown", handleInteraction, {capture: true});
            node.removeEventListener("wheel", handleInteraction, {capture: true});
            node.removeEventListener("touchstart", handleInteraction, {capture: true});
        };
    }, [trackMapInteractionOnce]);

    useEffect(() => {
        let isActive = true;

        getRoutes({longitude, latitude, radius: ROUTES_RADIUS}).then((result) => {
            if (isActive) {
                setRoutesGrouped(result);
            }
        });

        return () => {
            isActive = false;
        };
    }, [longitude, latitude]);

    const poiStats = statsData?.poi ?? null;

    const routes = useMemo(
        () =>
            routesGrouped
                ? {
                      busRoutes: routesGrouped[IPublicTransportType.BUS] ?? null,
                      railwayRoutes: routesGrouped[IPublicTransportType.TRAIN] ?? null,
                      subwayRoutes: routesGrouped[IPublicTransportType.SUBWAY] ?? null,
                      tramRoutes: routesGrouped[IPublicTransportType.TRAM] ?? null
                  }
                : null,
        [routesGrouped]
    );

    const handleTransportRouteClick = (route: IPublicTransportRoute) => {
        trackMapInteractionOnce();
        setActivePoiRoute((prev) => (prev?.id === route.id ? null : route));
    };

    return (
        <section className={sectionStyle}>
            <Text variant="mini_header" as="h3" color="var(--colors-secondary)">
                Lokalizacja
            </Text>

            <div ref={mapWrapperRef} className={mapWrapperStyle}>
                <OpenStreetMapsWithPoi
                    className={mapStyle}
                    offer={property.offer}
                    viewType={ViewType.PROPERTY}
                    initialPoiTypes={[PoiType.TRANSPORT]}
                    mapConfig={{scrollWheelZoom: false, fitBounds: true}}
                    disablePoiSwitch
                    disableInitiallyOpenedPoiId
                    clusterMarkers
                    showTransportLines
                    transportRoutes={routesGrouped}
                    activePoiRoute={activePoiRoute}
                />
            </div>

            <LocationPoisSummary
                offer={property.offer}
                country={property.offer.region.country}
                subwayPoi={(poiStats?.subway_poi?.id && poiStats.subway_poi) || null}
                railwayPoi={(poiStats?.railway_poi?.id && poiStats.railway_poi) || null}
                tramPoi={(poiStats?.tram_poi?.id && poiStats.tram_poi) || null}
                busPoi={(poiStats?.bus_poi?.id && poiStats.bus_poi) || null}
                nearestPoisCount={poiStats?.nearest_pois_count}
                routes={routes}
                activePoiRoute={activePoiRoute}
                onTransportRouteClick={handleTransportRouteClick}
                nearbySubject="nieruchomości"
            />
        </section>
    );
}

const sectionStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    md: {
        pt: 3
    }
});

const mapWrapperStyle = css({
    position: "relative",
    width: "100%",
    height: "260px",
    minHeight: "260px",
    maxHeight: "260px",
    overflow: "hidden",
    border: "1px solid var(--colors-gray-200)",
    isolation: "isolate",
    contain: "layout paint",
    md: {
        height: "320px",
        minHeight: "320px",
        maxHeight: "320px"
    },
    "& > div": {
        width: "100%",
        height: "100%"
    },
    "& .leaflet-container": {
        width: "100%",
        height: "100%"
    }
});

const mapStyle = css({
    width: "100%",
    height: "100%",
    minHeight: "100%",
    maxHeight: "100%"
});
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {css} from "@panda/css";

import {Text} from "@pg-design/text-module";

import {IOSMPoisRoutesGrouped} from "../../../maps/actions/fetch_osm_pois_routes_grouped";
import {useGetOfferDetailPoiStatsQuery} from "../../../maps/api/get_offer_detail_poi";
import {getRoutes} from "../../../maps/api/get_routes";
import {LocationPoisSummary} from "../../../maps/components/LocationPoisSummary";
import {OpenStreetMapsWithPoi} from "../../../maps/components/map_with_poi/OpenStreetMapsWithPoi";
import {IPublicTransportRoute} from "../../../maps/types/IPublicTransportRoute";
import {IPublicTransportType} from "../../../maps/types/IPublicTransportType";
import {PoiType} from "../../../maps/utils/PoiType";
import {IPropertyDetail} from "../../../property/types/IPropertyDetail";
import {ViewType} from "../../../view_type/ViewType";
import {trackTinderMultiLeadMapInteraction} from "./tracking/track_tinder_multi_lead_events";

const ROUTES_RADIUS = 3000;

interface IProps {
    property: IPropertyDetail;
}

export function TinderMultiLeadLocationSection({property}: IProps) {
    const offerId = property.offer.id;
    const longitude = property.offer.geo_point.coordinates[0];
    const latitude = property.offer.geo_point.coordinates[1];
    const {data: statsData} = useGetOfferDetailPoiStatsQuery(offerId);
    const [activePoiRoute, setActivePoiRoute] = useState<IPublicTransportRoute | null>(null);
    const [routesGrouped, setRoutesGrouped] = useState<IOSMPoisRoutesGrouped | null>(null);
    const mapInteractionTrackedRef = useRef(false);
    const mapWrapperRef = useRef<HTMLDivElement>(null);

    const trackMapInteractionOnce = useCallback(() => {
        if (mapInteractionTrackedRef.current) {
            return;
        }

        mapInteractionTrackedRef.current = true;
        trackTinderMultiLeadMapInteraction({property});
    }, [property]);

    useEffect(() => {
        const node = mapWrapperRef.current;

        if (!node) {
            return;
        }

        const handleInteraction = () => trackMapInteractionOnce();

        node.addEventListener("pointerdown", handleInteraction, {capture: true});
        node.addEventListener("wheel", handleInteraction, {capture: true, passive: true});
        node.addEventListener("touchstart", handleInteraction, {capture: true, passive: true});

        return () => {
            node.removeEventListener("pointerdown", handleInteraction, {capture: true});
            node.removeEventListener("wheel", handleInteraction, {capture: true});
            node.removeEventListener("touchstart", handleInteraction, {capture: true});
        };
    }, [trackMapInteractionOnce]);

    useEffect(() => {
        let isActive = true;

        getRoutes({longitude, latitude, radius: ROUTES_RADIUS}).then((result) => {
            if (isActive) {
                setRoutesGrouped(result);
            }
        });

        return () => {
            isActive = false;
        };
    }, [longitude, latitude]);

    const poiStats = statsData?.poi ?? null;

    const routes = useMemo(
        () =>
            routesGrouped
                ? {
                      busRoutes: routesGrouped[IPublicTransportType.BUS] ?? null,
                      railwayRoutes: routesGrouped[IPublicTransportType.TRAIN] ?? null,
                      subwayRoutes: routesGrouped[IPublicTransportType.SUBWAY] ?? null,
                      tramRoutes: routesGrouped[IPublicTransportType.TRAM] ?? null
                  }
                : null,
        [routesGrouped]
    );

    const handleTransportRouteClick = (route: IPublicTransportRoute) => {
        trackMapInteractionOnce();
        setActivePoiRoute((prev) => (prev?.id === route.id ? null : route));
    };

    return (
        <section className={sectionStyle}>
            <Text variant="mini_header" as="h3" color="var(--colors-secondary)">
                Lokalizacja
            </Text>

            <div ref={mapWrapperRef} className={mapWrapperStyle}>
                <OpenStreetMapsWithPoi
                    className={mapStyle}
                    offer={property.offer}
                    viewType={ViewType.PROPERTY}
                    initialPoiTypes={[PoiType.TRANSPORT]}
                    mapConfig={{scrollWheelZoom: false, fitBounds: true}}
                    disablePoiSwitch
                    disableInitiallyOpenedPoiId
                    clusterMarkers
                    showTransportLines
                    transportRoutes={routesGrouped}
                    activePoiRoute={activePoiRoute}
                />
            </div>

            <LocationPoisSummary
                offer={property.offer}
                country={property.offer.region.country}
                subwayPoi={(poiStats?.subway_poi?.id && poiStats.subway_poi) || null}
                railwayPoi={(poiStats?.railway_poi?.id && poiStats.railway_poi) || null}
                tramPoi={(poiStats?.tram_poi?.id && poiStats.tram_poi) || null}
                busPoi={(poiStats?.bus_poi?.id && poiStats.bus_poi) || null}
                nearestPoisCount={poiStats?.nearest_pois_count}
                routes={routes}
                activePoiRoute={activePoiRoute}
                onTransportRouteClick={handleTransportRouteClick}
                nearbySubject="nieruchomości"
            />
        </section>
    );
}

const sectionStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    md: {
        pt: 3
    }
});

const mapWrapperStyle = css({
    position: "relative",
    width: "100%",
    height: "260px",
    minHeight: "260px",
    maxHeight: "260px",
    overflow: "hidden",
    border: "1px solid var(--colors-gray-200)",
    isolation: "isolate",
    contain: "layout paint",
    md: {
        height: "320px",
        minHeight: "320px",
        maxHeight: "320px"
    },
    "& > div": {
        width: "100%",
        height: "100%"
    },
    "& .leaflet-container": {
        width: "100%",
        height: "100%"
    }
});

const mapStyle = css({
    width: "100%",
    height: "100%",
    minHeight: "100%",
    maxHeight: "100%"
});
